// src/pages/ApplicationChampionshipSeason/shared/steps/Step2Stages/GroupStagesPreview.tsx
import { FunctionComponent } from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { SeasonFormData } from '../../schemas';
import { GroupStageChip } from './GroupStageChip';
import { QualifierZonesCheatSheet } from './QualifierZonesCheatSheet';

const PREFIX = 'pages.application_season.components.season_step2.groups';
const QUALIFIERS_PREFIX = 'pages.application_season.components.season_step2.qualifiers';

/** Caixa com as fases de grupo geradas automaticamente a partir de `groups_count`. */
export const GroupStagesPreview: FunctionComponent = () => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<SeasonFormData>();
  const { fields } = useFieldArray({ control, name: 'group_stages' });

  /** Copia os 4 nomes de zona do primeiro grupo para todos os demais. */
  const applyFirstGroupLabelsToAll = () => {
    const groups = getValues('group_stages');
    if (!groups?.length) return;
    const first = groups[0];
    groups.forEach((_, index) => {
      if (index === 0) return;
      setValue(`group_stages.${index}.direct_qualifiers_label`, first.direct_qualifiers_label ?? '', {
        shouldDirty: true,
      });
      setValue(`group_stages.${index}.playoff_qualifiers_label`, first.playoff_qualifiers_label ?? '', {
        shouldDirty: true,
      });
      setValue(
        `group_stages.${index}.secondary_qualifiers_label`,
        first.secondary_qualifiers_label ?? '',
        { shouldDirty: true },
      );
      setValue(
        `group_stages.${index}.relegated_qualifiers_label`,
        first.relegated_qualifiers_label ?? '',
        { shouldDirty: true },
      );
    });
  };

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
      <Text
        fontSize="12px"
        fontWeight="semibold"
        color="gray.500"
        letterSpacing="wide"
        mb={3}
      >
        {t(`${PREFIX}.box_title`)}
      </Text>
      <Flex direction="column" gap={3}>
        <Box>
          <Text fontSize="13px" fontWeight="semibold" color="gray.700" mb={1}>
            {t(`${QUALIFIERS_PREFIX}.section_title_group`)}
          </Text>
          <Text fontSize="12px" color="gray.500">
            {t(`${QUALIFIERS_PREFIX}.section_help_group`)}
          </Text>
        </Box>
        <QualifierZonesCheatSheet />
        {fields.length > 1 ? (
          <Button
            variant="link"
            size="sm"
            alignSelf="flex-start"
            color="brand.500"
            leftIcon={<ChakraFeatherIcon name="copy" boxSize="13px" />}
            onClick={applyFirstGroupLabelsToAll}
          >
            {t(`${QUALIFIERS_PREFIX}.apply_labels_to_all_groups`)}
          </Button>
        ) : null}
        <Flex wrap="wrap" gap={3}>
          {fields.map((field, index) => (
            <GroupStageChip key={field.id} index={index} hideCheatSheet />
          ))}
        </Flex>
      </Flex>
    </Box>
  );
};
